/**
 * Keyboard — native keyboard handling for Capacitor WebView.
 *
 * Prevents the common issue where the mobile keyboard covers
 * form inputs. Also provides programmatic keyboard control.
 *
 * Works on both iOS and Android.
 */

import { isIOS, isNativePlatform } from '@/lib/utils/platform'

type KeyboardListener = { remove: () => void }

/**
 * Elements that should NOT trigger keyboard dismiss when tapped.
 * Matches: form inputs, interactive controls, and container widgets
 * like comboboxes / listboxes where tapping an option shouldn't
 * dismiss the keyboard before the selection registers.
 */
const INTERACTIVE_SELECTOR = [
  'input',
  'textarea',
  'select',
  'button',
  'a',
  'label',
  '[contenteditable]',
  '[role="combobox"]',
  '[role="listbox"]',
  '[role="option"]',
  '[role="dialog"]',
  '[role="menu"]',
  '[role="menuitem"]',
  '[data-vaul-drawer]',
  '[data-vaul-drawer-overlay]',
  '[data-radix-popper-content-wrapper]',
  '.PhoneInputCountrySelect',
  '.PhoneInputCountrySelectArrow',
].join(',')

/**
 * Check if a touch target (or any of its ancestors) is an interactive
 * element that should keep the keyboard open.
 */
function isInteractiveElement(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false
  // Direct match or ancestor match
  return target.closest(INTERACTIVE_SELECTOR) !== null
}

/**
 * Initialize keyboard handling for the native app.
 *
 * - Automatically scrolls focused inputs into view when the keyboard opens
 * - Adds tap-to-dismiss: tapping non-interactive areas hides the keyboard
 * - Enables the iOS accessory bar (Done / Prev / Next)
 *
 * Call once from a root client component (NativeAppShell).
 * @returns Cleanup function to remove listeners
 */
export async function initKeyboardHandling(): Promise<() => void> {
  if (!isNativePlatform()) return () => {}

  try {
    const { Keyboard } = await import('@capacitor/keyboard')
    const listeners: KeyboardListener[] = []
    const cleanups: (() => void)[] = []

    // ── 1. Enable iOS accessory bar (Done / Prev / Next) ──────────
    // By default Capacitor hides it. The "Done" button is the #1
    // iOS mechanism for dismissing a keyboard.
    // iOS only: Android has no accessory bar and rejects with UNIMPLEMENTED, which
    // aborted this whole setup (auto-scroll and tap-to-dismiss never registered).
    if (isIOS()) await Keyboard.setAccessoryBarVisible({ isVisible: true })

    // ── 2. Auto-scroll focused input into view on keyboard show ───
    const showListener = await Keyboard.addListener('keyboardWillShow', (info) => {
      const activeEl = document.activeElement as HTMLElement | null
      if (!activeEl) return

      // Calculate if the element is hidden behind the keyboard
      const rect = activeEl.getBoundingClientRect()
      const visibleHeight = window.innerHeight - info.keyboardHeight
      const buffer = 20 // px above the element

      if (rect.bottom > visibleHeight) {
        // scrollIntoView with 'center' works best across iOS/Android
        activeEl.scrollIntoView({ behavior: 'smooth', block: 'center' })
      } else if (rect.top < 0) {
        // Element is above viewport (e.g. after scroll correction)
        activeEl.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }

      // Ensure window also adjusts if scrollIntoView doesn't suffice
      requestAnimationFrame(() => {
        const newRect = activeEl.getBoundingClientRect()
        const newVisibleHeight = window.innerHeight - info.keyboardHeight
        if (newRect.bottom > newVisibleHeight) {
          const scrollAmount = newRect.bottom - newVisibleHeight + buffer
          window.scrollBy({ top: scrollAmount, behavior: 'smooth' })
        }
      })
    })
    listeners.push(showListener)

    // ── 3. Keyboard did hide ──────────────────────────────────────
    const hideListener = await Keyboard.addListener('keyboardWillHide', () => {
      // No manual cleanup needed — KeyboardResize.Body handles layout
      // restoration automatically. We keep this listener for future
      // extensions (e.g. analytics, scroll restoration).
    })
    listeners.push(hideListener)

    // ── 4. Tap-to-dismiss: blur active element on a tap outside interactive UI ──
    cleanups.push(attachTapToDismiss(document))

    return () => {
      listeners.forEach((l) => l.remove())
      cleanups.forEach((fn) => fn())
    }
  } catch {
    return () => {}
  }
}

/**
 * Set keyboard accessory bar visibility (iOS only).
 * The accessory bar is the bar with "Done" / navigation arrows
 * above the keyboard.
 *
 * @param visible - true to show, false to hide
 */
export async function setAccessoryBarVisible(visible: boolean): Promise<void> {
  if (!isNativePlatform()) return

  try {
    const { Keyboard } = await import('@capacitor/keyboard')
    await Keyboard.setAccessoryBarVisible({ isVisible: visible })
  } catch {
    // Non-critical, iOS only
  }
}

/** A touch that moves more than this many px is a scroll or swipe, not a tap. */
const TAP_MAX_MOVE_PX = 10
/** A touch held longer than this (ms) is a long press, not a tap. */
const TAP_MAX_DURATION_MS = 500

/**
 * Blur the focused text field (closing the keyboard) when the user TAPS outside
 * interactive UI. The decision is made on touchend: a finger that starts a scroll
 * in a chat, or a long press, must not close the keyboard.
 * Returns the cleanup function.
 */
export function attachTapToDismiss(doc: Document): () => void {
  let start: { x: number; y: number; at: number; target: EventTarget | null } | null = null
  let moved = false

  const onStart = (e: TouchEvent) => {
    const t = e.touches[0]
    if (!t || e.touches.length > 1) {
      start = null
      return
    }
    start = { x: t.clientX, y: t.clientY, at: Date.now(), target: e.target }
    moved = false
  }

  const onMove = (e: TouchEvent) => {
    if (!start) return
    const t = e.touches[0]
    if (!t) return
    if (Math.hypot(t.clientX - start.x, t.clientY - start.y) > TAP_MAX_MOVE_PX) moved = true
  }

  const onEnd = () => {
    const begun = start
    start = null
    if (!begun || moved || Date.now() - begun.at > TAP_MAX_DURATION_MS) return

    const activeEl = doc.activeElement
    if (
      !activeEl ||
      (activeEl.tagName !== 'INPUT' &&
        activeEl.tagName !== 'TEXTAREA' &&
        !(activeEl as HTMLElement).isContentEditable)
    ) {
      return
    }
    if (isInteractiveElement(begun.target)) return
    ;(activeEl as HTMLElement).blur()
  }

  const onCancel = () => {
    start = null
  }

  doc.addEventListener('touchstart', onStart, { passive: true })
  doc.addEventListener('touchmove', onMove, { passive: true })
  doc.addEventListener('touchend', onEnd, { passive: true })
  doc.addEventListener('touchcancel', onCancel, { passive: true })
  return () => {
    doc.removeEventListener('touchstart', onStart)
    doc.removeEventListener('touchmove', onMove)
    doc.removeEventListener('touchend', onEnd)
    doc.removeEventListener('touchcancel', onCancel)
  }
}
